import {
  catchErrorHandler,
  format24HourTime,
  removeLocalStorageData,
} from "@picovico/utils";
import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store";

import { AlertProps, ButtonProps } from "@chakra-ui/react";
import { useAppToast } from "@/hooks/useAppToast";
import { useWebFeature } from "@/hooks/useWebFeature";
import SeatSelector from "@components/SeatSelector";
import { AlertMessage } from "@picovico/ui";
import { SHOW_BOOKING_CLOSED } from "@picovico/ui/constant/alertMessage";
import { format } from "date-fns";
import Error from "next/error";
import { useRouter } from "next/router";
import { getPageRoutes } from "routes/pageRoutes";
import { useCreateSeatBookingMutation } from "store/services/booking";
import { loadSeatsAsync } from "store/slices";

function SeatBooking({
  proceedRoute,
  actionButtonProps: configActionButtonProps,
  bookingClosedAlertProps,
  ...props
}: {
  proceedRoute: "food";
  actionButtonProps?: ButtonProps;
  bookingClosedAlertProps?: AlertProps;
}) {
  const router = useRouter();
  const isMounted = useRef(false);
  const showIdx = router?.query?.showIdx as string;
  const movieIdx = router?.query?.movieIdx as string;
  const [createBooking, { isLoading: isBooking }] =
    useCreateSeatBookingMutation();
  const { seats, isLoading, error, showDetail, currentSelected } =
    useAppSelector((state) => state.showSeatList);
  const { isLoggedIn } = useAppSelector((state) => state.user);
  // No food step while the company has web food ordering off: straight to confirm.
  const foodOrderingEnabled = useWebFeature("concession.web_food_ordering");

  const dispatch = useAppDispatch();
  const { error: errorToast } = useAppToast();

  useEffect(() => {
    if (showIdx && !isMounted.current) {
      dispatch(loadSeatsAsync(showIdx.toString()));
      isMounted.current = true;
    }
    return () => {
      isMounted.current = false;
    };
  }, [dispatch, showIdx]);

  const handleLogin = useCallback(() => {
    const currentUrl = router.asPath;
    router.push(getPageRoutes("login", {}, { next: currentUrl }));
  }, [router]);

  const handleBookingProceed = useCallback(() => {
    if (showIdx) {
      const { movieHall, movieShow } = currentSelected;
      removeLocalStorageData("booking_no");

      createBooking({
        movie_show: movieShow,
      })
        .unwrap()
        .then(() => {
          if (proceedRoute === "food" && foodOrderingEnabled) {
            router.push(
              getPageRoutes(
                "food_booking",
                {
                  movieIdx,
                  showIdx: movieShow,
                },
                {
                  cinema_hall: movieHall,
                },
              ),
            );
          } else {
            router.push(
              getPageRoutes(
                "confirm_booking",
                {
                  movieIdx,
                  showIdx: movieShow,
                },
                {
                  cinema_hall: movieHall,
                },
              ),
            );
          }
        })
        .catch((err) => {
          catchErrorHandler(err, errorToast);
        });
    }
  }, [
    showIdx,
    currentSelected,
    createBooking,
    router,
    movieIdx,
    errorToast,
    proceedRoute,
    foodOrderingEnabled,
  ]);

  if (router.isReady && !showIdx)
    return (
      <Error statusCode={404} title="This show is not available anymore" />
    );
  // The company does not allow viewing seats once the booking window closes.
  if ((error as { status?: number } | null)?.status === 403)
    return (
      <Error
        statusCode={403}
        title="Booking window has closed. Seat viewing is not available."
      />
    );

  // Booking window closed: the seat layout stays visible with live seat
  // statuses, but nothing can be selected or booked.
  const isBookingClosed = Boolean(showDetail?.lockWeb);

  return (
    <>
      {isBookingClosed && (
        <AlertMessage
          status="warning"
          message={SHOW_BOOKING_CLOSED}
          {...bookingClosedAlertProps}
        />
      )}
      <SeatSelector
        isLoading={isLoading || !router.isReady}
        disableSelectingSeats={isBooking || isBookingClosed}
        actionButton={isLoggedIn ? "Proceed" : "Login"}
        actionButtonProps={{
          ...configActionButtonProps,
          onClick: isLoggedIn ? handleBookingProceed : handleLogin,
          isLoading: isBooking,
          isDisabled: isBookingClosed
            ? true
            : isLoggedIn
              ? !currentSelected.selectedSeats.length || isBooking
              : false,
        }}
        seatLayouts={seats || []}
        movieShowDetail={{
          title: showDetail?.movieTitle || "",
          date: showDetail?.movieDate
            ? format(new Date(showDetail?.movieDate), "PP")
            : "",
          time: showDetail?.movieTime
            ? format24HourTime(showDetail?.movieTime)
            : "",
          theater: showDetail?.cinemaHall || "",
          release_date: showDetail?.release_date || "",
          genre: showDetail?.genre || "",
          grade: showDetail?.grade || "",
          duration: showDetail?.duration || 0,
        }}
        showDetail={{
          cinemaHall: showDetail?.cinemaHall || "",
          screenName: showDetail?.screenName || "",
          showIdx: showDetail?.showIdx || "",
          cinemaHallIdx: showDetail?.cinemaHallIdx || "",
          audiIdx: showDetail?.audiIdx || "",
          audi_layout: showDetail?.audi_layout || "col",
          seat_selected_time: showDetail?.seat_selected_time || null,
          location: showDetail?.location || "",
          three_d_price: showDetail?.three_d_price,
        }}
        screenPosition={showDetail?.audiScreenPosition as "Top" | "Bottom"}
        {...props}
      />
    </>
  );
}

export default SeatBooking;
